import React from 'react';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { isSameDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import { getWeekStartEnv } from 'helpers/envGetters';
import { palette, animations } from '../../theme';
import { MobileDatePickerProps } from './MobileDatePicker';

const WEEK_START = getWeekStartEnv();

const wrapperStyles = {
  width: '100%',
  height: '500px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflowY: 'auto',
  paddingBottom: '40px',
  ...animations.appearanceEffect,
};

// Calculate dynamic week number based on current month
export const calculateWeekNumber = (date: Date) => {
  const currentDate = date || new Date();
  const firstDayOfMonth = startOfMonth(currentDate);
  const lastDayOfMonth = endOfMonth(currentDate);

  // Get the first day of the first week and last day of the last week that contain this month
  const firstWeekStart = startOfWeek(firstDayOfMonth, { weekStartsOn: WEEK_START });
  const lastWeekEnd = endOfWeek(lastDayOfMonth, { weekStartsOn: WEEK_START });

  // Calculate the difference in weeks
  const diffInMs = lastWeekEnd.getTime() - firstWeekStart.getTime();
  const diffInWeeks = Math.ceil(diffInMs / (7 * 24 * 60 * 60 * 1000));

  // Ensure reasonable bounds (typically 4-6 weeks for a month)
  return Math.max(4, Math.min(6, diffInWeeks));
};

const MobileDayPicker = (props: MobileDatePickerProps) => {
  const { startDate, endDate, selectedDay, setActiveDate, closeCalendarModal } = props;

  const dynamicWeekNumber = calculateWeekNumber(selectedDay);

  const onChangeDateHandler = (date: Date | null) => {
    if (!date || isSameDay(startDate || new Date(), date)) {
      return;
    }
    setActiveDate?.(date);
    closeCalendarModal?.();
  };

  // Custom day renderer to make selected date text white
  const renderDayWithWhiteText = (pickersDayProps: any) => {
    const { day, selected, ...other } = pickersDayProps;

    return (
      <PickersDay
        {...other}
        day={day}
        selected={selected}
        sx={{
          ...(selected && {
            backgroundColor: palette.primary.main,
            color: palette.background.paper,
            '&:hover': {
              backgroundColor: palette.primary.main,
              color: palette.background.paper,
            },
            '&.Mui-selected': {
              backgroundColor: palette.primary.main,
              color: palette.background.paper,
            },
            '&.Mui-selected:hover': {
              backgroundColor: palette.primary.main,
              color: palette.background.paper,
            },
          }),
        }}
      />
    );
  };

  return (
    <Box sx={wrapperStyles} data-id={'OuterWrapper'}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={{
          ...enLocale,
          options: {
            ...enLocale.options,
            weekStartsOn: WEEK_START,
          },
        }}>
        <DateCalendar
          value={selectedDay || null}
          onChange={onChangeDateHandler}
          minDate={startDate}
          maxDate={endDate}
          showDaysOutsideCurrentMonth={true}
          fixedWeekNumber={dynamicWeekNumber}
          slots={{
            day: renderDayWithWhiteText,
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};
export default MobileDayPicker;
